import React, { useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Wand2, FileText, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const suggestions = [
  "Which receptionist has the best conversion rate this month?",
  "Show me all patient dues overdue by 30+ days.",
  "Which receptionist needs coaching on upsell?",
  "Summarise complaint trends this month.",
];

const AIAssistant = () => {
  const [q, setQ] = useState("");
  const [ans, setAns] = useState("");
  const [loading, setLoading] = useState(false);
  const [monthly, setMonthly] = useState("");
  const [monthlyLoading, setMonthlyLoading] = useState(false);

  const ask = async (question) => {
    const text = question || q;
    if (!text.trim()) return;
    setLoading(true);
    setAns("");
    try {
      const { data } = await api.post("/ai/query", { question: text });
      setAns(data.answer);
      setQ(text);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI request failed");
    } finally {
      setLoading(false);
    }
  };

  const genMonthly = async () => {
    setMonthlyLoading(true);
    setMonthly("");
    try {
      const { data } = await api.post("/ai/monthly-summary", {});
      setMonthly(data.summary);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI request failed");
    } finally {
      setMonthlyLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="overline">Reception Intelligence</div>
        <h1 className="font-heading text-4xl text-white tracking-tight mt-1 flex items-center gap-3">
          AI Assistant
          <Sparkles className="text-[#C9A24B]" size={28} />
        </h1>
        <p className="text-slate-400 mt-2 max-w-2xl">
          Powered by Claude Sonnet 4.5. Ask natural-language questions about reception performance, dues, complaints — or generate an auto-drafted monthly summary for the founder.
        </p>
      </div>

      <Card className="bg-gradient-to-br from-[#0F172A] to-[#1a2235] border-[#C9A24B]/40 p-6 gold-glow">
        <div className="flex items-center gap-2 mb-4">
          <Wand2 size={18} className="text-[#C9A24B]" />
          <h3 className="font-heading text-xl text-white">Natural Language Query</h3>
        </div>
        <div className="flex flex-col md:flex-row gap-3">
          <Input
            data-testid="ai-question-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && ask()}
            placeholder="Ask anything about reception operations…"
            className="bg-[#0F172A] border-[#334155] text-white h-11"
          />
          <Button
            onClick={() => ask()}
            data-testid="ai-ask-btn"
            disabled={loading}
            className="bg-[#C9A24B] hover:bg-[#D4AF60] text-[#0F172A] font-medium h-11 px-6"
          >
            {loading ? "Thinking…" : "Ask"}
          </Button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {suggestions.map((s) => (
            <button
              key={s}
              onClick={() => ask(s)}
              data-testid={`ai-suggestion-${s.slice(0, 10)}`}
              className="text-xs px-3 py-1.5 rounded-full bg-[#1E293B] border border-[#334155] text-slate-300 hover:border-[#C9A24B] hover:text-[#C9A24B] transition-colors"
            >
              {s}
            </button>
          ))}
        </div>
        {ans && (
          <div className="mt-6 p-5 rounded-lg bg-[#0B1120] border border-[#334155] whitespace-pre-wrap text-slate-200 leading-relaxed font-body" data-testid="ai-answer">
            {ans}
          </div>
        )}
      </Card>

      <Card className="bg-[#1E293B] border-[#334155] p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileText size={18} className="text-[#C9A24B]" />
            <h3 className="font-heading text-xl text-white">Monthly Founder Summary</h3>
          </div>
          <Button
            onClick={genMonthly}
            data-testid="ai-monthly-btn"
            disabled={monthlyLoading}
            className="bg-[#C9A24B] hover:bg-[#D4AF60] text-[#0F172A] font-medium"
          >
            {monthlyLoading ? "Generating…" : "Generate Now"}
          </Button>
        </div>
        {monthly ? (
          <div className="p-5 rounded-lg bg-[#0F172A] border border-[#334155] whitespace-pre-wrap text-slate-200 leading-relaxed" data-testid="ai-monthly-summary">
            {monthly}
          </div>
        ) : (
          <div className="p-5 rounded-lg bg-[#0F172A] border border-dashed border-[#334155] text-sm text-slate-500">
            <TrendingUp size={18} className="text-[#C9A24B] mb-2" />
            Click "Generate Now" to auto-draft this month's reception summary — revenue, feedback trend, complaint resolution rate, compliance score, best & weakest performer per profile, and recommended actions.
          </div>
        )}
      </Card>
    </div>
  );
};

export default AIAssistant;
